import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fuseConfig } from 'app/fuse-config';
import { FuseConfig } from '@fuse/types';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '@fuse/services/auth/auth.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

@Injectable()
export class ProfileService implements Resolve<any>, OnDestroy {

    private _unsubscribeAll: Subject<any>;
    private _fuseConfig: FuseConfig;

    routeParams: any;
    profile: any;
    onProfileChanged: BehaviorSubject<any>;
    onMemberImageChanged: BehaviorSubject<any>;
    onProfileDemogsChanged: BehaviorSubject<any>;
    onMemberFilesChanged: BehaviorSubject<any>;
    onMemberCvChanged: BehaviorSubject<any>;
    onMemberSkillsChanged: BehaviorSubject<any>;
    onSkillGroupsChanged: BehaviorSubject<any>;
    onMemberProjectsChanged: BehaviorSubject<any>;
    onMemberChildrenChange: BehaviorSubject<any>;
    onMemberLanguageChange: BehaviorSubject<any>;
    onMemberWorkExperienceChange: BehaviorSubject<any>;
    onMemberEducationChange: BehaviorSubject<any>;

    /**
     * Constructor
     *
     * @param {HttpClient} _http
     */
    constructor(
        private _http: HttpClient,
        private _router: Router,
        private sanitizer: DomSanitizer,
        private _translationLoader: FuseTranslationLoaderService
    ) {
        this._fuseConfig = fuseConfig;
        this._unsubscribeAll = new Subject();

        // Set the defaults
        this.onProfileChanged = new BehaviorSubject({});
        this.onMemberImageChanged = new BehaviorSubject({});
        this.onProfileDemogsChanged = new BehaviorSubject({});
        this.onMemberFilesChanged = new BehaviorSubject({});
        this.onMemberCvChanged = new BehaviorSubject({});
        this.onMemberSkillsChanged = new BehaviorSubject({});
        this.onSkillGroupsChanged = new BehaviorSubject({});
        this.onMemberProjectsChanged = new BehaviorSubject({});
        this.onMemberChildrenChange = new BehaviorSubject({});
        this.onMemberLanguageChange = new BehaviorSubject({});
        this.onMemberWorkExperienceChange = new BehaviorSubject({});
        this.onMemberEducationChange = new BehaviorSubject({});
    }

    /**
     * Resolver
     *
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<any> | Promise<any> | any}
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {
        this.routeParams = route.params;

        return new Promise<void>((resolve, reject) => {

            Promise.all([
                this.getProfile(),
                this.getProfileDemogs(),
                this.getMemberProfileImage(),
                this.getProfileAssetList(),
                this.getMemberCv(),
                this.getSkillGroups(),
                this._translationLoader.getMultipleTranslationCategories(localStorage.getItem('lang') || 'en', [6, 14, 15, 16, 17, 18])
            ])
                .then(
                    () => {
                        resolve();
                    }
                )
                .catch(err => Promise.reject(err))
                .then(() => { }, err => {
                    this._router.navigate(['/home']);
                });
        });
    }

    public getProfile(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/me')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.profile = response;
                        this.onProfileChanged.next(this.profile);
                        resolve(response);
                    }, reject);
        });
    }

    public getMemberProfileImage(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/asset/profile', { responseType: 'blob' })
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberImageChanged.next(response);
                        resolve(response);
                    }, reject);
        });

    }

    public updateProfile(params: any): Promise<any> {

        // const params = {
        //     email: email,
        //     phone_number: phoneNumber,
        //     first_name: firstName,
        //     last_name: lastName,
        //     title: title,
        //     date_of_birth: dateOfBirth,
        //     address: address
        // };

        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/update', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.getProfile();
                        resolve(response);
                    }, reject);
        });
    }

    public listUserProjects(
        page: number = 1, limit: number = 20, orderBy: string = 'id',
        order: string = 'asc', search: string = '', lang: string = localStorage.getItem('lang') || 'sr'
    ): Promise<any> {

        const params = {
            limit: limit,
            order_by: orderBy,
            order: order,
            search: search,
            lang: lang
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/project/listing', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberProjectsChanged.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public getProfileDemogs(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/demog/get-all')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.profile = response;
                        this.onProfileDemogsChanged.next(this.profile);
                        resolve(response);
                    }, reject);
        });
    }

    public getEnumOptions(id: number): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/demog/get-enum-options/' + id)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }


    public getAllCitiesList(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/public/country-city')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public updateProfileDemog(id: number, value: any): Promise<any> {

        const params = {
            demog_id: id,
            value: value
        };

        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/demog/update', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.getProfileDemogs();
                        resolve(response);
                    }, reject);
        });
    }

    public getProfileAssetList(collection: string = 'doc'): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/asset/listing?collection=' + collection)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberFilesChanged.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public downloadMemberAsset(itemId: number, collection: string, fileName: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/asset/get/' + collection + '/' + itemId, { responseType: 'blob' as 'json' })
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        const dataType = response['type'];
                        const binaryData = [];
                        binaryData.push(response);
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
                        downloadLink.setAttribute('download', fileName);
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                    }, reject);
        });

    }

    public deleteMemberAsset(itemId: number, collection: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.delete(this._fuseConfig.api.url + '/portal/asset/delete/' + collection + '/' + itemId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        switch (collection) {
                            case 'cv':
                                this.getProfileAssetList('cv');
                                break;
                            case 'doc':
                                this.getProfileAssetList();
                                break;
                        }
                        resolve(response);
                    }, reject);
        });

    }

    public depersonalizer(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/depersonalizer', { confirmed: 'yes' })
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public getMemberCv(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/asset/cv')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberCvChanged.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public downloadMemberCv(fileName: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/asset/cv?dl=yes', { responseType: 'blob' as 'json' })
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        const dataType = response['type'];
                        const binaryData = [];
                        binaryData.push(response);
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
                        downloadLink.setAttribute('download', fileName);
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                    }, reject);
        });

    }

    public deleteMemberCv(itemId: number, collection: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.delete(this._fuseConfig.api.url + '/portal/asset/delete/' + collection + '/' + itemId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        switch (collection) {
                            case 'cv':
                                this.getMemberCv();
                                break;
                            case 'doc':
                                this.getProfileAssetList();
                                break;
                        }
                        resolve(response);
                    }, reject);
        });

    }

    public getSkills(group: string, diff: string = 'no'): Promise<any> {

        const params = {
            group: group,
            diff: diff
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/skill/listing', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        // Refresh results for member only if regular data is got, if invert then do not refresh
                        if (diff === 'no') {
                            this.onMemberSkillsChanged.next({ group: group, data: response });
                        }
                        resolve(response);
                    }, reject);
        });

    }

    public getSkillGroups(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/skill/groups')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onSkillGroupsChanged.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public addSkillToMember(skillId: number, group: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/skill/add/' + skillId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.getSkills(group);
                        resolve(response);
                    }, reject);
        });
    }

    public removeSkillFromMember(skillId: number, group: string): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/skill/remove/' + skillId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.getSkills(group);
                        resolve(response);
                    }, reject);
        });
    }

    public editMyPassword(passwordData: any): Promise<any> {
        const params = {
            password: passwordData.password,
            confirm_password: passwordData.confirmPassword,
            old_password: passwordData.oldPassword
        };
        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/update-my-password', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        console.log('Edit password');
                        console.log(response);
                        resolve(response);
                    }, reject);
        });
    }

    public createMyPassword(passwordData: any): Promise<any> {
        const params = {
            password: passwordData.password,
            confirm_password: passwordData.confirmPassword
        };
        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/create-password', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        console.log('Create password');
                        console.log(response);
                        resolve(response);
                    }, reject);
        });
    }

    public detachFromSocial(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/detach-social-account')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        console.log('Detached');
                        console.log(response);
                        resolve(response);
                        this.getProfile();
                    }, reject);
        });
    }

    public attachToSocial(token, source): Promise<any> {

        const params = {
            token: token,
            source: source
        };
        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/attach-social-account', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        console.log('Attached');
                        console.log(response);
                        resolve(response);
                        this.getProfile();
                    }, reject);
        });
    }

    public listMemberChildren(
        orderBy: string = 'id', order: string = 'desc',
        search: string = ''): Promise<any> {

        const params = {
            order_by: orderBy,
            order: order,
            search: search
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/children/get-all', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberChildrenChange.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public getMemberChildren(childrenId: number): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/children/get-data/' + childrenId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public getValidGendersOfMemberChildren(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/children/get-gender')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public listMemberSpokenLanguages(
        orderBy: string = 'id', order: string = 'desc',
        search: string = ''): Promise<any> {

        const params = {
            order_by: orderBy,
            order: order,
            search: search
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/spoken-language/get-all', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberLanguageChange.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public getMemberSpokenLanguages(spokenLangId: number): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/spoken-language/get-data/' + spokenLangId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public getValidLangsOfSpokenLanguage(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/spoken-language/get-languages')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public getValidLevelsOfSpokenLanguage(): Promise<any> {

        return new Promise((resolve, reject) => {
            this._http.get(this._fuseConfig.api.url + '/portal/member/spoken-language/get-levels')
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public listMemberWorkExperience(
        orderBy: string = 'id', order: string = 'desc',
        search: string = ''): Promise<any> {

        const params = {
            order_by: orderBy,
            order: order,
            search: search
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/work-experience/get-all', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberWorkExperienceChange.next(response);
                        resolve(response);
                    }, reject);
        });
    }

    public listMemberEducation(
        orderBy: string = 'id', order: string = 'desc',
        search: string = ''): Promise<any> {

        const params = {
            order_by: orderBy,
            order: order,
            search: search
        };

        return new Promise((resolve, reject) => {
            this._http.post(this._fuseConfig.api.url + '/portal/member/education/get-all', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        this.onMemberEducationChange.next(response);
                        resolve(response);
                    }, reject);
        });
    }




    /**
     * Lifecycle hooks
     */

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
